/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';

import EditIconFilled from '@mui/icons-material/Edit';
import LanguageIcon from '@mui/icons-material/Language';
import CheckIcon from '@mui/icons-material/Check';
import Text from '@mui/material/Typography';

import Stepper from '@mui/material/Stepper';
import MuiStep from '@mui/material/Step';
import Label from '@mui/material/StepLabel';

import { useIsMobile } from '../../utils/hooks';

function Step({
  active,
  done,
  idx,
  activeIcon,
  inActiveIcon,
  title,
  isLast = false,
}) {
  const { t } = useTranslation();
  return (
    <div
      css={css`
        display: flex;
        gap: 0.25rem;
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 51px;
        `}
      >
        <div
          css={css`
            height: 51px;
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
          `}
        >
          {active ? (
            <div
              css={css`
                display: flex;
                justify-content: center;
                align-items: center;

                border-radius: 50%;
                width: 48px;
                height: 48px;
                border: 2px solid var(--primary-main);
              `}
            >
              <div
                css={css`
                  background: var(--primary-main);
                  width: 36px;
                  height: 36px;
                  border-radius: 50%;
                  color: white;

                  display: flex;
                  justify-content: center;
                  align-items: center;
                `}
              >
                {activeIcon}
              </div>
            </div>
          ) : done ? (
            <div
              css={css`
                display: flex;
                justify-content: center;
                align-items: center;
                background-color: var(--success);
                color: white;
                border-radius: 50%;
                padding: 4px;
                margin-left: 11px;
                margin-right: 11px;
              `}
            >
              <CheckIcon fontSize="small" />
            </div>
          ) : inActiveIcon ? (
            <div
              css={css`
                width: 40px;
                height: 40px;
                border: 1px solid rgba(0, 0, 0, 0.23);
                border-radius: 50%;

                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;

                color: rgba(0, 0, 0, 0.23);
                margin-left: 5.5px;
                margin-right: 5.5px;
              `}
            >
              {inActiveIcon}
            </div>
          ) : (
            <div
              css={css`
                width: 16px;
                height: 16px;
                border: 1px solid rgba(0, 0, 0, 0.23);
                border-radius: 50%;
                margin-left: 17.5px;
                margin-right: 17.5px;
              `}
            ></div>
          )}
        </div>
        {!isLast && (
          <div
            css={css`
              height: 24px;
              width: 49px;
              margin: 8px 0;
              display: flex;
              justify-content: center;
            `}
          >
            <div
              css={css`
                height: 100%;
                width: 2px;
                background-color: ${done ? '#2E7D32' : 'rgba(0, 0, 0, 0.23)'};
              `}
            />
          </div>
        )}
      </div>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          margin-left: 8px;
        `}
      >
        <Text variant="caption" weight="400">
          {t('STEG {{index}}', { index: idx + 1 })}
        </Text>
        <Text
          css={css`
            font-family: 'Lato', sans-serif;
            font-size: 15px;
            font-style: normal;
            font-weight: 600;
            line-height: 150%; /* 22.5px */
            letter-spacing: 0.15px;
            color: ${!active ? '#00000099' : 'inherit'};
          `}
          variant="body2"
        >
          {title}
        </Text>
      </div>
    </div>
  );
}

export default function ProgressStepper({ currentStep }) {
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();

  const steps = [
    {
      title: t('Välj ett språk'),
      activeIcon: <LanguageIcon />,
    },
    {
      title: t('Hur kommer du arbeta?'),
      activeIcon: <EditIconFilled fontSize="small" />,
    },
    {
      title: t('Hur hittade du hit?'),
      activeIcon: <EditIconFilled />,
    },
    {
      title: t('Vem är du?'),
      activeIcon: <EditIconFilled />,
    },
    {
      title: t('Välj en plan'),
      activeIcon: <EditIconFilled />,
    },
  ];

  if (!isMobile) {
    return (
      <div
        css={css`
          width: 280px;
        `}
      >
        {steps.map((step, idx) => (
          <Step
            active={idx === currentStep}
            done={idx < currentStep}
            idx={idx}
            title={step.title}
            activeIcon={step.activeIcon}
            inActiveIcon={step.inactiveIcon}
            isLast={idx === steps.length - 1}
          />
        ))}
      </div>
    );
  }

  return (
    <div>
      <Stepper
        css={css`
          width: 100%;
          padding: 0;
        `}
        activeStep={currentStep}
        variant="dots"
        alternativeLabel
      >
        {steps.map((step, idx) => (
          <MuiStep
            active={idx === currentStep}
            done={idx < currentStep}
            idx={idx}
            title={step.title}
            activeIcon={step.activeIcon}
            inActiveIcon={step.inactiveIcon}
            isLast={idx === steps.length - 1}
          >
            <Label> </Label>
          </MuiStep>
        ))}
      </Stepper>
    </div>
  );
}
